import { FormEvent, KeyboardEvent, ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { PaperAirplaneIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { pageLabel } from '../config/assistantPrimer'
import useEscapeKey from '../hooks/useEscapeKey'
import { assistantAPI } from '../services/api'
import type { AssistantTurn } from '../types'

export const ASSISTANT_AVATAR_SRC = `${process.env.PUBLIC_URL ?? ''}/Fincreator.webp`

// Mirror backend services/assistant_service.py, which rejects anything longer.
export const MAX_QUESTION_CHARS = 1000
const MAX_HISTORY_TURNS = 10
const MAX_TURN_CHARS = 2000

const SUGGESTIONS = ['What does EV mean?', 'How is confidence calculated?', 'What is the Track Record page for?']

interface ChatMessage extends AssistantTurn {
  id: number
  /** A failure notice shown in the thread; never sent back as history. */
  failed?: boolean
}

// The portrait art is about twice as tall as it is wide; this framing keeps
// the robot's head inside the circle.
const Avatar = ({ className }: { className: string }) => (
  <img src={ASSISTANT_AVATAR_SRC} alt="" className={`shrink-0 rounded-full object-cover object-[50%_24%] ${className}`} />
)

const AssistantBubble = ({ children, failed = false }: { children: ReactNode; failed?: boolean }) => (
  <div className="flex items-start gap-2">
    <Avatar className="h-8 w-8" />
    <p
      className={`max-w-[85%] whitespace-pre-wrap break-words rounded-lg rounded-tl-sm px-3 py-2 text-sm ${
        failed ? 'border border-amber-500/40 bg-amber-500/10 text-amber-200' : 'bg-terminal-raised text-gray-100'
      }`}
    >
      {children}
    </p>
  </div>
)

// Rate-limit and availability responses carry a message written for people.
const failureMessage = (error: unknown): string => {
  const response = (error as { response?: { status?: number; data?: { message?: unknown } } } | null)?.response
  if ((response?.status === 429 || response?.status === 503) && typeof response.data?.message === 'string') {
    return response.data.message
  }
  return "Sorry, I couldn't get an answer just now. Please try again."
}

/**
 * The FinCreator assistant: a robot avatar fixed to the bottom-right that
 * opens a chat about the app's terms, features and pages. It knows the app,
 * not the account, and tells the backend which page is open so "this page"
 * questions resolve.
 */
const AssistantChat = () => {
  const location = useLocation()
  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [draft, setDraft] = useState('')
  const [sending, setSending] = useState(false)
  const nextId = useRef(0)
  const toggleRef = useRef<HTMLButtonElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const threadRef = useRef<HTMLDivElement>(null)

  const close = useCallback(() => {
    setOpen(false)
    toggleRef.current?.focus()
  }, [])
  useEscapeKey(close, open)

  useEffect(() => {
    if (open) inputRef.current?.focus()
  }, [open])

  useEffect(() => {
    const thread = threadRef.current
    if (thread) thread.scrollTop = thread.scrollHeight
  }, [messages, sending, open])

  const append = (message: Omit<ChatMessage, 'id'>) =>
    setMessages((prev) => [...prev, { ...message, id: nextId.current++ }])

  const send = async (text: string) => {
    const question = text.trim()
    if (!question || sending) return
    const history = messages
      .filter((message) => !message.failed)
      .slice(-MAX_HISTORY_TURNS)
      .map(({ role, content }) => ({ role, content: content.slice(0, MAX_TURN_CHARS) }))

    append({ role: 'user', content: question })
    setDraft('')
    setSending(true)
    try {
      const { answer } = await assistantAPI.ask({ question, history, page: pageLabel(location.pathname) })
      append({ role: 'assistant', content: answer })
    } catch (error) {
      append({ role: 'assistant', content: failureMessage(error), failed: true })
    } finally {
      setSending(false)
    }
  }

  const onSubmit = (event: FormEvent) => {
    event.preventDefault()
    send(draft)
  }

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault()
      send(draft)
    }
  }

  return (
    <>
      {open && (
        <section
          id="assistant-panel"
          role="dialog"
          aria-label="FinCreator assistant"
          className="fixed bottom-24 right-4 z-30 flex h-[min(34rem,calc(100vh-8rem))] w-[calc(100vw-2rem)] max-w-sm flex-col overflow-hidden rounded-xl border border-terminal-border bg-terminal-surface text-gray-100 shadow-2xl [color-scheme:dark]"
        >
          <header className="flex items-center gap-3 border-b border-terminal-border bg-terminal-raised px-4 py-3">
            <Avatar className="h-9 w-9" />
            <div className="min-w-0 flex-1">
              <h2 className="text-sm font-semibold text-gray-50">FinCreator Assistant</h2>
              <p className="text-xs text-gray-400">Explains terms, features and pages</p>
            </div>
            <button
              type="button"
              onClick={close}
              aria-label="Close assistant"
              className="rounded-md p-1.5 text-gray-400 hover:bg-white/5 hover:text-gray-200"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </header>

          <div ref={threadRef} aria-live="polite" className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
            <AssistantBubble>
              Hi! I can explain FinCreator's terms, features and what each page does. I can't see your account data
              or take actions for you.
            </AssistantBubble>
            {messages.length === 0 && (
              <div className="flex flex-wrap gap-2 pl-10">
                {SUGGESTIONS.map((suggestion) => (
                  <button
                    key={suggestion}
                    type="button"
                    onClick={() => send(suggestion)}
                    className="rounded-full border border-terminal-border px-3 py-1 text-xs text-gray-300 hover:border-primary-500 hover:text-gray-100"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}
            {messages.map((message) =>
              message.role === 'user' ? (
                <div key={message.id} className="flex justify-end">
                  <p className="max-w-[85%] whitespace-pre-wrap break-words rounded-lg rounded-br-sm bg-primary-600 px-3 py-2 text-sm text-white">
                    {message.content}
                  </p>
                </div>
              ) : (
                <AssistantBubble key={message.id} failed={message.failed}>
                  {message.content}
                </AssistantBubble>
              )
            )}
            {sending && (
              <AssistantBubble>
                <span className="text-gray-400">Thinking…</span>
              </AssistantBubble>
            )}
          </div>

          <form onSubmit={onSubmit} className="border-t border-terminal-border p-3">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                rows={2}
                value={draft}
                maxLength={MAX_QUESTION_CHARS}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={onKeyDown}
                aria-label="Ask the assistant"
                placeholder="Ask about a term, feature or page…"
                className="flex-1 resize-none rounded-md border border-terminal-border bg-terminal-raised px-3 py-2 text-sm text-gray-100 placeholder:text-gray-500 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
              <button
                type="submit"
                disabled={sending || !draft.trim()}
                aria-label="Send"
                className="rounded-md bg-primary-600 p-2.5 text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <PaperAirplaneIcon className="h-5 w-5" />
              </button>
            </div>
            <p className="mt-2 text-[11px] text-gray-500">AI-generated answers about the app. Not financial advice.</p>
          </form>
        </section>
      )}

      <button
        ref={toggleRef}
        type="button"
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        aria-expanded={open}
        aria-controls="assistant-panel"
        aria-label={open ? 'Hide FinCreator assistant' : 'Open FinCreator assistant'}
        title="FinCreator assistant"
        className="fixed bottom-4 right-4 z-30 h-16 w-16 rounded-full shadow-lg ring-2 ring-primary-500 transition hover:scale-105 focus:outline-none focus-visible:ring-4"
      >
        <Avatar className="h-full w-full" />
      </button>
    </>
  )
}

export default AssistantChat
